import { usePostHog } from '@posthog/react';
import { useEffect, useRef } from 'react';

export type TSupportChatSource = 'launcher' | 'in_app';

export const ZENDESK_POLL_MS = 500;
export const ZENDESK_MAX_POLLS = 120;

let isRegistered = false;
let pendingSource: TSupportChatSource | undefined;

/**
 * Marks where the next support chat open starts.
 *
 * @param source - Where the open starts.
 */
export function setSupportChatSource(source: TSupportChatSource): void {
  pendingSource = source;
}

/**
 * Sends a PostHog event with the current page each time the Zendesk messenger opens.
 * GTM loads Zendesk asynchronously, so this waits for `window.zE`.
 *
 * @param accountId - The current account id.
 */
function useSupportChatAnalytics(accountId?: string): void {
  const posthog = usePostHog();
  const posthogRef = useRef(posthog);
  const accountIdRef = useRef(accountId);
  posthogRef.current = posthog;
  accountIdRef.current = accountId;

  useEffect(() => {
    let timer: number | undefined;
    let polls = 0;

    function register(): void {
      // Zendesk has no way to remove a handler, so register one per page load.
      if (isRegistered) {
        return;
      }
      if (typeof window.zE !== 'function') {
        polls += 1;
        if (polls < ZENDESK_MAX_POLLS) {
          timer = window.setTimeout(register, ZENDESK_POLL_MS);
        }
        return;
      }
      isRegistered = true;
      window.zE('messenger:on', 'open', () => {
        const source = pendingSource ?? 'launcher';
        pendingSource = undefined;
        posthogRef.current?.capture('support_chat_opened', {
          source,
          path: window.location.pathname,
          account_id: accountIdRef.current,
        });
      });
      // The launcher toggles, so an in-app click can close the messenger instead of opening it.
      window.zE('messenger:on', 'close', () => {
        pendingSource = undefined;
      });
    }

    register();
    return () => window.clearTimeout(timer);
  }, []);
}

/** Resets module state between tests. */
export function resetSupportChatAnalytics(): void {
  isRegistered = false;
  pendingSource = undefined;
}

export default useSupportChatAnalytics;
